const NON_METAFIELD_FACET_SOURCES = {
  part_type: {
    label: 'Part Type',
    values: record => (record.partTypeHandle ? [[record.partTypeHandle, record.partType]] : []),
  },
  sub_brand_name: {
    label: 'Sub Brand',
    values: record => (record.subBrandNameHandle ? [[record.subBrandNameHandle, record.subBrandName]] : []),
  },
  availability: {
    label: 'Availability',
    values: record => [record.available ? ['in-stock', 'In stock'] : ['out-of-stock', 'Out of stock']],
  },
  price: {
    label: 'Price',
    type: 'price',
  },
  vendor: {
    label: 'Brand',
    values: record => (record.vendor ? [[window.handleizeFacet(record.vendor), record.vendor]] : []),
  },
  tags: {
    label: 'Tags',
    values: record => (record.tags || []).map(tag => [window.handleizeFacet(tag), tag]),
  },
};

const parseFacetSource = source => {
  const raw = String(source || '').trim().toLowerCase();
  const dot = raw.indexOf('.');
  if (dot === -1) return { namespace: 'pdm', key: raw, id: `pdm.${raw}` };
  return { namespace: raw.slice(0, dot), key: raw.slice(dot + 1), id: raw };
};

const facetKeyForSource = source => {
  const raw = String(source || '').trim().toLowerCase();
  const stripped = raw.indexOf('pdm.') === 0 ? raw.slice(4) : raw;
  return stripped.replace(/[.-]/g, '_');
};

const DEFAULT_FACETS = [
  { key: 'part_type', source: 'part_type', order: 10 },
  { key: 'sub_brand_name', source: 'sub_brand_name', order: 20 },
  { key: 'availability', source: 'availability', order: 40 },
  { key: 'price', source: 'price', order: 50 },
];

const metafieldFacetValues = (record, source) => {
  const raw = record.metafields && record.metafields[parseFacetSource(source).id];
  if (raw === undefined || raw === null || raw === '') return [];

  let values = [raw];
  if (typeof raw === 'string' && raw.trim().charAt(0) === '[') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) values = parsed;
    } catch (e) {
      values = [raw];
    }
  }

  return values
    .map(value => String(value).trim())
    .filter(Boolean)
    .map(value => [window.handleizeFacet(value), value]);
};

const VEHICLE_SORT_OPTIONS = [
  { value: 'manual', name: 'Featured' },
  { value: 'title-ascending', name: 'Alphabetically, A-Z' },
  { value: 'title-descending', name: 'Alphabetically, Z-A' },
  { value: 'price-ascending', name: 'Price, low to high' },
  { value: 'price-descending', name: 'Price, high to low' },
  { value: 'created-ascending', name: 'Date, old to new' },
  { value: 'created-descending', name: 'Date, new to old' },
];

window.handleizeFacet = value =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const CHECK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M9 2L4.1875 7L2 4.72727" stroke="white" stroke-width="1.5" stroke-linecap="square"/></svg>';

const REMOVE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>';

class VehicleFacets {
  constructor(options) {
    this.grid = options.grid;
    this.records = options.records;
    this.container = options.container || document.createElement('div');
    this.config = Object.assign(
      {
        fallbackRank: 1000000000,
        defaultSort: 'manual',
        enableFiltering: false,
        enableSorting: false,
        enableDisplayOrder: false,
        pairRank: {},
      },
      window.vehicleSortConfig || {}
    );

    this.facetsEl = this.container.querySelector('[data-vehicle-facets]');
    this.sortEl = this.container.querySelector('[data-vehicle-sort]');
    this.countEl = this.container.querySelector('[data-filters-bar-results-count]');
    this.appliedCountEl = this.container.querySelector('[data-filters-applied]');
    this.appliedMenuEl = this.container.querySelector('[data-active-filters="menu"]');
    this.appliedBarEl = this.container.querySelector('[data-active-filters="filters-bar"]');
    this.appliedTitleEl = this.container.querySelector('[data-applied-filters-title]');
    this.clearAllEl = this.container.querySelector('[data-vehicle-clear-all]');
    this.sortLabelEls = this.container.querySelectorAll('[data-sortby-menu-active], [data-filters-active-sort]');
    this.visualFilterEl = document.querySelector('[data-visual-filters]');
    this.visualFilterLinks = this.visualFilterEl
      ? Array.from(this.visualFilterEl.querySelectorAll('[data-visual-filter]'))
      : [];
    this.visualFilterTiles = this.visualFilterLinks.map(link => {
      const labels = (link.dataset.visualFilterLabels || '').split(',').map(label => label.trim());
      return {
        link,
        labels,
        values: (link.dataset.visualFilterValues || '').split(',').filter(Boolean),
        name: link.querySelector('.visual-filters__label')?.textContent?.trim() || labels.join(' & '),
      };
    });
    this.visualFilterTrack = this.visualFilterEl
      ? this.visualFilterEl.querySelector('.visual-filters__carousel .visual-filters__items')
      : null;

    this.records.forEach((record, index) => this.decorate(record, index));

    this.buildFacets();
    this.state = this.readState();
    this.lockedPartTypes = this.readLockedPartTypes();
    this.renderFacets();
    this.renderSort();
    this.bind();
    this.apply();
  }

  decorate(record, index) {
    record.partTypeHandle = window.handleizeFacet(record.partType);
    record.subBrandNameHandle = window.handleizeFacet(record.subBrandName);
    record.searchText = [record.title, record.partType, record.subBrandName].join(' ').toLowerCase();

    record.rankPartTypeHandle = window.handleizeFacet(record.pdmPartType || record.partType);

    const pairKey = record.rankPartTypeHandle ? `${record.rankPartTypeHandle}::${record.subBrandNameHandle}` : '';

    record.index = index;
    record.displayRank = this.displayRank(pairKey);
    record.sortKey = [record.displayRank, record.title.toLowerCase()];
  }

  displayRank(pairKey) {
    const rank = pairKey ? this.config.pairRank[pairKey] : undefined;
    return typeof rank === 'number' ? rank : this.config.fallbackRank;
  }

  buildFacetDefs() {
    const configured = (this.config.facets || []).filter(facet => facet && facet.source);
    const source = configured.length ? configured : DEFAULT_FACETS;

    return source
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(facet => {
        const builtin = NON_METAFIELD_FACET_SOURCES[facet.source];
        return {
          key: facet.key || facetKeyForSource(facet.source),
          label: facet.label || (builtin && builtin.label) || facet.source,
          type: builtin && builtin.type === 'price' ? 'price' : 'list',
          getValues: builtin && builtin.values ? builtin.values : record => metafieldFacetValues(record, facet.source),
        };
      });
  }

  seedSelectedTileValues(values) {
    if (!this.visualFilterTiles.length) return;

    const raw = new URLSearchParams(window.location.search).get('part_type');
    const selected = raw ? raw.split(',').filter(Boolean) : [];
    if (!selected.length) return;

    this.visualFilterTiles.forEach(tile => {
      tile.values.forEach((value, index) => {
        if (!selected.includes(value) || values.has(value)) return;
        values.set(value, { value, label: (tile.labels[index] || value).trim(), count: 0 });
      });
    });
  }

  buildFacets() {
    const filtering = this.config.enableFiltering !== false;

    this.facetDefs = filtering ? this.buildFacetDefs() : [];
    this.priceFacet = this.facetDefs.find(def => def.type === 'price') || null;

    this.facets = this.facetDefs.filter(def => def.type === 'list').map(def => {
      const values = new Map();
      this.records.forEach(record => {
        def.getValues(record).forEach(([value, label]) => {
          if (!value) return;
          if (!values.has(value)) values.set(value, { value, label, count: 0 });
        });
      });
      if (def.key === 'part_type') this.seedSelectedTileValues(values);
      const sorted = Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      return { key: def.key, label: def.label, getValues: def.getValues, values: sorted };
    }).filter(facet => facet.values.length > 1);

    const prices = this.priceFacet
      ? this.records.map(record => record.price).filter(price => typeof price === 'number')
      : [];
    this.priceMax = prices.length ? Math.ceil(Math.max(...prices)) : 0;
  }

  /*
    Filtering can be turned off while ?part_type= is still in the URL, and the part type facet is also dropped
    when every product shares one part type, so keep honouring the param when no facet is there to own it
  */
  readLockedPartTypes() {
    if (this.facets.some(facet => facet.key === 'part_type')) return [];
    const raw = new URLSearchParams(window.location.search).get('part_type');
    return raw ? raw.split(',').filter(Boolean) : [];
  }

  matchesLockedPartTypes(record) {
    if (!this.lockedPartTypes.length) return true;

    const cardEl = record.el.matches('[data-product-card]') ? record.el : record.el.querySelector('[data-product-card]');
    const handle = record.partTypeHandle || window.handleizeFacet(cardEl?.dataset.type);
    return this.lockedPartTypes.includes(handle);
  }

  matches(record, ignoreKey) {
    return this.facets.every(facet => {
      if (facet.key === ignoreKey) return true;
      const selected = this.state.filters[facet.key];
      if (!selected || !selected.length) return true;
      const recordValues = facet.getValues(record).map(pair => pair[0]);
      return selected.some(value => recordValues.includes(value));
    });
  }

  inPriceRange(record) {
    const { priceMin, priceMax } = this.state;
    if (priceMin !== null && record.price < priceMin) return false;
    if (priceMax !== null && record.price > priceMax) return false;
    return true;
  }

  visibleRecords() {
    return this.records.filter(record => this.matchesLockedPartTypes(record) && this.matches(record) && this.inPriceRange(record));
  }

  syncCheckedAttributes() {
    this.container
      .querySelectorAll('[data-vehicle-facets] input[type="checkbox"], [data-vehicle-sort] input[type="radio"]')
      .forEach(input => input.toggleAttribute('checked', input.checked));
  }

  apply() {
    this.syncCheckedAttributes();
    const visible = this.visibleRecords();
    const visibleHandles = new Set(visible.map(record => record.handle));

    this.records.forEach(record => {
      record.el.classList.toggle('hide', !visibleHandles.has(record.handle));
    });

    const comparator = this.comparator(this.state.sort);
    const sorted = visible.slice().sort(comparator);
    const fragment = document.createDocumentFragment();
    sorted.forEach(record => fragment.appendChild(record.el));
    this.grid.appendChild(fragment);

    this.updateCounts(visible);
    this.updateAppliedFilters();
    this.updateVisualFilters();
    this.updateSortLabel();
    this.writeState();

    this.container.closest('vehicle-products')?.classList.toggle('vehicle-products--no-results', visible.length === 0);
    document.dispatchEvent(new CustomEvent('vehicle:facets:applied', { detail: { count: visible.length } }));
  }

  comparator(sort) {
    switch (sort) {
      case 'price-ascending':
        return (a, b) => a.price - b.price || a.title.localeCompare(b.title);
      case 'price-descending':
        return (a, b) => b.price - a.price || a.title.localeCompare(b.title);
      case 'title-ascending':
        return (a, b) => a.title.localeCompare(b.title);
      case 'title-descending':
        return (a, b) => b.title.localeCompare(a.title);
      case 'created-ascending':
        return (a, b) => a.createdAt - b.createdAt || a.title.localeCompare(b.title);
      case 'created-descending':
        return (a, b) => b.createdAt - a.createdAt || a.title.localeCompare(b.title);
      default:
        if (this.config.enableDisplayOrder === false) return (a, b) => a.index - b.index;
        return (a, b) => this.compareKeys(a.sortKey, b.sortKey);
    }
  }

  compareKeys(a, b) {
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] === b[i]) continue;
      if (typeof a[i] === 'string') return a[i].localeCompare(b[i]);
      return a[i] - b[i];
    }
    return 0;
  }

  updateCounts(visible) {
    this.facets.forEach(facet => {
      const pool = this.records.filter(record => this.matchesLockedPartTypes(record) && this.matches(record, facet.key) && this.inPriceRange(record));
      facet.values.forEach(entry => {
        entry.count = pool.filter(record => facet.getValues(record).map(pair => pair[0]).includes(entry.value)).length;
        const input = this.facetsEl && this.facetsEl.querySelector(`input[name="${facet.key}"][value="${entry.value}"]`);
        if (!input) return;
        input.disabled = entry.count === 0 && !input.checked;
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('disabled', input.disabled);
      });
    });

    if (this.countEl) {
      this.countEl.innerHTML =
        visible.length === 0 ? '<span class="no-results">Sorry no results</span>' : `${visible.length} products`;
    }
  }

  isTileActive(tileValues, selected) {
    return tileValues.length > 0 && tileValues.every(value => selected.includes(value));
  }

  stackedPartTypes(tileValues, selected) {
    if (this.isTileActive(tileValues, selected)) return selected.filter(value => !tileValues.includes(value));
    return selected.concat(tileValues.filter(value => !selected.includes(value)));
  }

  updateVisualFilters() {
    if (!this.visualFilterTiles.length) return;

    const selected = this.state.filters.part_type || [];
    const pool = this.records.filter(record => this.matchesLockedPartTypes(record) && this.matches(record, 'part_type') && this.inPriceRange(record));
    const visibleSlides = [];
    let available = 0;

    this.visualFilterTiles.forEach(tile => {
      const { link } = tile;
      const count = pool.filter(record => tile.values.includes(record.partTypeHandle)).length;
      const isActive = this.isTileActive(tile.values, selected);

      const countEl = link.querySelector('[data-visual-filter-count]');
      if (countEl) countEl.textContent = `(${count})`;

      if (isActive) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');

      const isEmpty = count === 0 && !isActive;
      const slide = link.closest('.visual-filters__item');
      if (!isEmpty) {
        available += 1;
        if (slide) visibleSlides.push(slide);
      }

      const params = new URLSearchParams();
      const stacked = this.stackedPartTypes(tile.values, selected);
      if (stacked.length) params.set('part_type', stacked.join(','));
      if (this.state.sort && this.state.sort !== this.config.defaultSort) params.set('sort_by', this.state.sort);
      const query = this.toQuery(params);
      link.setAttribute('href', `${window.location.pathname}${query ? `?${query}` : ''}`);

      const label = link.querySelector('.visual-filters__label');
      if (label) {
        link.setAttribute(
          'aria-label',
          `${label.textContent.trim()}, ${count} ${count === 1 ? 'product' : 'products'}${isActive ? ', selected' : ''}`
        );
      }
    });

    if (this.visualFilterTrack) {
      const current = Array.from(this.visualFilterTrack.children);
      const changed =
        current.length !== visibleSlides.length || visibleSlides.some((slide, index) => current[index] !== slide);

      if (changed) this.visualFilterTrack.replaceChildren(...visibleSlides);
    }

    this.visualFilterEl?.classList.remove('visual-filters--loading');

    const swiper = this.visualFilterEl?.querySelector('.swiper')?.swiper;
    if (swiper) {
      swiper.recalcSlides?.();
      swiper.update();
    }

    if (this.visualFilterEl) this.visualFilterEl.hidden = available === 0;
  }

  selectVisualFilter(values) {
    if (!values.length) return;
    const stacked = this.stackedPartTypes(values, this.state.filters.part_type || []);

    if (stacked.length) this.state.filters.part_type = stacked;
    else delete this.state.filters.part_type;

    this.renderFacets();
    this.apply();
  }

  appliedTileGroups(facet) {
    if (facet.key !== 'part_type') return { tiles: [], covered: [] };

    const selected = this.state.filters.part_type || [];
    const tiles = [];
    const covered = [];

    this.visualFilterTiles.forEach(tile => {
      if (tile.values.length < 2 || !this.isTileActive(tile.values, selected)) return;
      if (tile.values.some(value => covered.includes(value))) return;
      tiles.push(tile);
      covered.push(...tile.values);
    });

    return { tiles, covered };
  }

  updateAppliedFilters() {
    const chips = [];
    this.facets.forEach(facet => {
      const { tiles, covered } = this.appliedTileGroups(facet);

      tiles.forEach(tile => {
        chips.push(
          `<button type="button" class="filters__active-btn" data-vehicle-filter-remove data-facet="${facet.key}" data-value="${tile.values.join(',')}" title="Remove ${tile.name} filter">${facet.label}: ${tile.name} ${REMOVE_ICON}</button>`
        );
      });

      (this.state.filters[facet.key] || []).forEach(value => {
        if (covered.includes(value)) return;
        const entry = facet.values.find(item => item.value === value);
        if (!entry) return;
        chips.push(
          `<button type="button" class="filters__active-btn" data-vehicle-filter-remove data-facet="${facet.key}" data-value="${value}" title="Remove ${entry.label} filter">${facet.label}: ${entry.label} ${REMOVE_ICON}</button>`
        );
      });
    });

    if (this.state.priceMin !== null || this.state.priceMax !== null) {
      const min = this.state.priceMin === null ? 0 : this.state.priceMin;
      const max = this.state.priceMax === null ? this.priceMax : this.state.priceMax;
      chips.push(
        `<button type="button" class="filters__active-btn" data-vehicle-filter-remove data-facet="price" title="Remove price filter">${min} - ${max} ${REMOVE_ICON}</button>`
      );
    }

    const markup = chips.join('');
    if (this.appliedMenuEl) this.appliedMenuEl.innerHTML = markup;
    if (this.appliedBarEl) this.appliedBarEl.innerHTML = markup;
    if (this.appliedCountEl) this.appliedCountEl.dataset.filtersApplied = chips.length;
    if (this.appliedTitleEl) this.appliedTitleEl.classList.toggle('hide', chips.length === 0);
    if (this.clearAllEl) this.clearAllEl.closest('.active-facets-vertical-filter')?.classList.toggle('hide', chips.length === 0);
  }

  updateSortLabel() {
    const active = VEHICLE_SORT_OPTIONS.find(option => option.value === this.state.sort) || VEHICLE_SORT_OPTIONS[0];
    this.sortLabelEls.forEach(el => {
      el.textContent = active.name;
    });
  }

  renderListGroup(facet, index) {
    const selected = this.state.filters[facet.key] || [];
    const items = facet.values
      .map((entry, i) => {
        const id = `VehicleFilter-${facet.key}-${i}`;
        const checked = selected.includes(entry.value) ? ' checked' : '';
        return `<div class="filters__group-item">
              <input class="filters__group-item-input" type="checkbox" name="${facet.key}" value="${entry.value}" id="${id}"${checked}>
              <label class="filters__group-item-label filters__label" for="${id}">
                <span class="filters__group-item-checkbox">${CHECK_ICON}</span>${entry.label}
              </label>
            </div>`;
      })
      .join('');

    const open = selected.length > 0 || window.openedFilters?.[facet.label] ? ' open' : '';
    return `<details class="filters__group accordion full-width hide-sort-menu${open ? ' open' : ''}" id="VehicleFacet-${index}" data-filter="${facet.label}"${open}
          ontoggle="window.openedFilters = window.openedFilters || {}; window.openedFilters['${facet.label}'] = this.open; this.classList[this.open ? 'add' : 'remove']('open');">
          <summary class="filters__group-title pseudo-plus-icon">${facet.label.toUpperCase()}</summary>
          <div class="filters__group-content">
            <div class="filters__group-items filters__group-items--filter filters__group-items--list grid">${items}</div>
          </div>
        </details>`;
  }

  renderPriceGroup(label) {
    const min = this.state.priceMin === null ? '' : this.state.priceMin;
    const max = this.state.priceMax === null ? '' : this.state.priceMax;
    return `<details class="filters__group accordion full-width hide-sort-menu" id="VehicleFacet-price" data-filter="${label}">
          <summary class="filters__group-title pseudo-plus-icon">${label.toUpperCase()}</summary>
          <div class="filters__group-content">
            <div class="filters__price">
              <price-range class="filters__price-range">
                <span class="field-currency">$</span>
                <div class="field">
                  <input class="field__input" type="number" min="0" max="${this.priceMax}" placeholder="0" name="price_min" id="VehicleFilter-price-min" value="${min}">
                  <label class="field__label" for="VehicleFilter-price-min">From</label>
                </div>
                <span class="field-currency">$</span>
                <div class="field">
                  <input class="field__input" type="number" min="0" max="${this.priceMax}" placeholder="${this.priceMax}" name="price_max" id="VehicleFilter-price-max" value="${max}">
                  <label class="field__label" for="VehicleFilter-price-max">To</label>
                </div>
              </price-range>
            </div>
          </div>
        </details>`;
  }

  renderFacets() {
    if (!this.facetsEl) return;

    const groups = this.facetDefs.map((def, index) => {
      if (def.type === 'price') return this.priceMax > 0 ? this.renderPriceGroup(def.label) : '';
      const facet = this.facets.find(item => item.key === def.key);
      return facet ? this.renderListGroup(facet, index) : '';
    });

    this.facetsEl.innerHTML = groups.join('');
  }

  renderSort() {
    if (!this.sortEl || this.config.enableSorting === false) return;
    this.sortEl.innerHTML = VEHICLE_SORT_OPTIONS.map(option => {
      const checked = option.value === this.state.sort ? ' checked' : '';
      return `<div class="filters__group-item filters__group-item--sort">
          <input class="filters__group-item-input" id="vehicle-sort-${option.value}" type="radio" name="sort_by" value="${option.value}"${checked}>
          <label class="filters__group-item-label" for="vehicle-sort-${option.value}">
            <span class="filters__group-item-checkbox">${CHECK_ICON}</span>${option.name}
          </label>
        </div>`;
    }).join('');
  }

  bind() {
    this.container.addEventListener(
      'change',
      event => {
        const input = event.target.closest('input');
        if (!input) return;
        event.stopPropagation();

        if (input.name === 'sort_by') {
          this.state.sort = input.value;
        } else if (input.name === 'price_min' || input.name === 'price_max') {
          const parsed = input.value === '' ? null : Number(input.value);
          this.state[input.name === 'price_min' ? 'priceMin' : 'priceMax'] = Number.isFinite(parsed) ? parsed : null;
        } else {
          const selected = new Set(this.state.filters[input.name] || []);
          if (input.checked) selected.add(input.value);
          else selected.delete(input.value);
          this.state.filters[input.name] = Array.from(selected);
        }

        this.apply();
      },
      true
    );

    this.container.addEventListener(
      'submit',
      event => {
        if (event.target.closest('[data-filters-form]')) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    document.addEventListener('click', event => {
      const visualEl = event.target.closest('[data-visual-filter]');
      if (visualEl && this.visualFilterEl && this.visualFilterEl.contains(visualEl)) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        const tile = this.visualFilterTiles.find(item => item.link === visualEl);
        if (tile && visualEl.getAttribute('aria-disabled') !== 'true') {
          this.selectVisualFilter(tile.values);
        }
        return;
      }

      const removeEl = event.target.closest('[data-vehicle-filter-remove]');
      const clearEl = event.target.closest('[data-vehicle-clear-all]');
      if (!removeEl && !clearEl) return;

      event.preventDefault();
      event.stopPropagation();

      if (clearEl) {
        this.state.filters = {};
        this.state.priceMin = null;
        this.state.priceMax = null;
      } else if (removeEl.dataset.facet === 'price') {
        this.state.priceMin = null;
        this.state.priceMax = null;
      } else {
        const { facet, value } = removeEl.dataset;
        const removed = value.split(',');
        this.state.filters[facet] = (this.state.filters[facet] || []).filter(item => !removed.includes(item));
      }

      this.renderFacets();
      this.apply();
    });
  }

  listFacetDefs() {
    return (this.facetDefs || []).filter(def => def.type === 'list');
  }

  toQuery(params) {
    return params.toString().replace(/%2C/g, ',');
  }

  readState() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    this.listFacetDefs().forEach(def => {
      const raw = params.get(def.key);
      if (raw) filters[def.key] = raw.split(',').filter(Boolean);
    });

    const toNumber = key => {
      const raw = params.get(key);
      if (raw === null || raw === '') return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const sortParam = params.get('sort_by');
    const isValidSort =
      this.config.enableSorting !== false && VEHICLE_SORT_OPTIONS.some(option => option.value === sortParam);

    return {
      filters,
      priceMin: toNumber('price_min'),
      priceMax: toNumber('price_max'),
      sort: isValidSort ? sortParam : this.config.defaultSort,
    };
  }

  writeState() {
    const params = new URLSearchParams(window.location.search);
    this.listFacetDefs().forEach(def => {
      const selected = this.state.filters[def.key];
      if (selected && selected.length) params.set(def.key, selected.join(','));
      else params.delete(def.key);
    });

    if (this.state.priceMin !== null) params.set('price_min', this.state.priceMin);
    else params.delete('price_min');
    if (this.state.priceMax !== null) params.set('price_max', this.state.priceMax);
    else params.delete('price_max');

    if (this.state.sort && this.state.sort !== this.config.defaultSort) params.set('sort_by', this.state.sort);
    else params.delete('sort_by');

    const query = this.toQuery(params);
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }
}

window.VehicleFacets = VehicleFacets;
